/**
 * WebAuthn Biometric Authentication Repository
 *
 * FERP Repository Pattern Compliance:
 * - NO direct API calls (all through GraphQL)
 * - Safety framework integration (inherited from BaseRepository)
 * - Immutable audit trail (inherited from BaseRepository)
 * - Multi-tenant isolation (inherited from BaseRepository)
 * - Data-driven architecture (all values from backend)
 *
 * Implements offline-first biometric authentication using WebAuthn (FIDO2):
 * - Registration: Enroll fingerprint/Face ID credentials
 * - Authentication: Login with biometric (returns JWT token)
 * - Management: List, delete registered devices
 *
 * Works 100% offline after initial registration (no internet, phone, or email required)
 */

import { BaseRepository } from './BaseRepository';
import type {
    PublicKeyCredentialCreationOptionsJSON,
    RegistrationResponseJSON,
    PublicKeyCredentialRequestOptionsJSON,
    AuthenticationResponseJSON,
} from '@simplewebauthn/browser';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';

/**
 * Biometric registration challenge from backend
 */
export interface BiometricRegistrationChallenge {
    /** JSON-encoded PublicKeyCredentialCreationOptions */
    challenge: string;
    /** User ID for session tracking */
    userId: string;
    /** Challenge expiration (5 minutes from creation) */
    expiresAt: string;
}

/**
 * Biometric authentication challenge from backend
 */
export interface BiometricAuthenticationChallenge {
    /** JSON-encoded PublicKeyCredentialRequestOptions */
    challenge: string;
    /** Session ID for state tracking */
    sessionId: string;
    /** Challenge expiration (5 minutes from creation) */
    expiresAt: string;
}

/**
 * Registered biometric credential (for device management)
 */
export interface BiometricCredential {
    /** Credential ID (base64url-encoded) */
    id: string;
    /** Human-readable device name (e.g., "iPhone 14 Pro", "MacBook Pro Touch ID") */
    deviceName?: string;
    /** Registration timestamp */
    createdAt: string;
    /** Last authentication timestamp */
    lastUsedAt?: string;
    /** Signature counter (increments on each use, prevents cloning) */
    counter: number;
    /** Whether this credential supports PRF extension */
    supportsPrf?: boolean;
}

// ============================================================================
// PRF Extension Types (FERP-PII-052: User-Controlled Encryption)
// ============================================================================

/**
 * Relying party info from backend
 */
export interface RelyingPartyInfo {
    id: string;
    name: string;
}

/**
 * WebAuthn user info from backend
 */
export interface WebAuthnUserInfo {
    id: string;
    name: string;
    displayName: string;
}

/**
 * Public key credential parameters
 */
export interface PubKeyCredParam {
    type: string;
    alg: number;
}

/**
 * Authenticator selection criteria
 */
export interface AuthenticatorSelectionInfo {
    authenticatorAttachment?: string;
    residentKey: string;
    userVerification: string;
}

/**
 * Allowed credential for authentication
 */
export interface AllowedCredentialInfo {
    type: string;
    id: string;
    transports?: string[];
}

/**
 * PRF registration options from backend (challenge/response flow)
 *
 * FERP-PII-052: Full WebAuthn options with PRF salt for enrollment
 */
export interface PrfRegistrationOptions {
    /** Session ID to include when finishing registration */
    sessionId: string;
    /** Challenge for WebAuthn (base64url) */
    challenge: string;
    /** PRF salt to use (base64) */
    prfSalt: string;
    /** Relying party information */
    rp: RelyingPartyInfo;
    /** User information */
    user: WebAuthnUserInfo;
    /** Credential parameters */
    pubKeyCredParams: PubKeyCredParam[];
    /** Timeout in milliseconds */
    timeout: number;
    /** Authenticator selection */
    authenticatorSelection: AuthenticatorSelectionInfo;
    /** Attestation preference */
    attestation: string;
}

/**
 * PRF authentication options from backend (challenge/response flow)
 *
 * FERP-PII-052: Full WebAuthn options with PRF salt for authentication
 */
export interface PrfAuthenticationOptions {
    /** Session ID to include when finishing authentication */
    sessionId: string;
    /** Challenge for WebAuthn (base64url) */
    challenge: string;
    /** PRF salt to use (base64) */
    prfSalt: string;
    /** Credential ID to authenticate with */
    credentialId: string;
    /** Allowed credentials */
    allowCredentials: AllowedCredentialInfo[];
    /** Timeout in milliseconds */
    timeout: number;
    /** User verification requirement */
    userVerification: string;
}

/**
 * PRF registration challenge from backend (legacy - kept for compatibility)
 *
 * FERP-PII-052: Includes salt for PRF evaluation during enrollment
 */
export interface PrfRegistrationChallenge {
    /** JSON-encoded PublicKeyCredentialCreationOptions with PRF extension */
    challenge: string;
    /** User ID for session tracking */
    userId: string;
    /** Challenge expiration (5 minutes from creation) */
    expiresAt: string;
    /** Salt for PRF evaluation (base64url-encoded, 32 bytes) */
    prfSalt: string;
}

/**
 * PRF authentication challenge from backend (legacy - kept for compatibility)
 *
 * FERP-PII-052: Includes salt for PRF evaluation during authentication
 */
export interface PrfAuthenticationChallenge {
    /** JSON-encoded PublicKeyCredentialRequestOptions with PRF extension */
    challenge: string;
    /** Session ID for state tracking */
    sessionId: string;
    /** Challenge expiration (5 minutes from creation) */
    expiresAt: string;
    /** Salt for PRF evaluation (base64url-encoded, 32 bytes) */
    prfSalt: string;
}

/**
 * PRF registration result
 *
 * FERP-PII-052: Contains credential info and PRF output for key derivation
 */
export interface PrfRegistrationResult {
    /** Success status */
    success: boolean;
    /** Credential ID (base64url-encoded) */
    credentialId: string;
    /** Device name */
    deviceName?: string;
    /** Registration timestamp */
    registeredAt: string;
    /** Whether PRF extension is supported by this credential */
    supportsPrf: boolean;
    /** PRF output (base64url-encoded, 32 bytes) - ONLY returned to client once during registration */
    prfOutput?: string;
}

/**
 * PRF authentication result
 *
 * FERP-PII-052: Contains JWT token and PRF output for decryption
 */
export interface PrfAuthenticationResult {
    /** Success status */
    success: boolean;
    /** JWT token for subsequent API requests (8-hour expiry) */
    token: string;
    /** Authenticated user ID */
    userId: string;
    /** User's tenant ID */
    tenantId: string;
    /** Token expiration timestamp */
    expiresAt: string;
    /** PRF output (base64url-encoded, 32 bytes) - used as key material for decryption */
    prfOutput?: string;
}

/**
 * Authentication result from backend
 */
export interface BiometricAuthenticationSuccess {
    /** Success status */
    success: boolean;
    /** JWT token for subsequent API requests (8-hour expiry) */
    token: string;
    /** Authenticated user ID */
    userId: string;
    /** User's tenant ID */
    tenantId: string;
    /** Token expiration timestamp */
    expiresAt: string;
}

/**
 * WebAuthn Repository
 *
 * Handles all biometric authentication operations using FERP repository pattern.
 * All data access flows through GraphQL (no direct HTTP/REST calls).
 */
export class WebAuthnRepository extends BaseRepository<any> {
    /**
     * Start biometric registration (Step 1 of 2)
     *
     * Initiates WebAuthn registration by requesting a challenge from the backend.
     *
     * **GitHub Issue #378:** Now requires tenantId for backend routing
     *
     * @param userId - User ID from existing account
     * @param username - Username for display in authenticator
     * @param displayName - User-friendly display name
     * @param tenantId - Tenant ID for backend routing (DynamoDB vs PostgreSQL)
     * @returns Registration challenge to pass to browser WebAuthn API
     */
    async startRegistration(
        userId: string,
        username: string,
        displayName: string,
        tenantId: string
    ): Promise<BiometricRegistrationChallenge> {
        const result = await this.mutation(
            `
            mutation StartBiometricRegistration(
                $userId: String!
                $username: String!
                $displayName: String!
                $tenantId: String!
            ) {
                startBiometricRegistration(input: {
                    userId: $userId
                    username: $username
                    displayName: $displayName
                    tenantId: $tenantId
                }) {
                    challenge
                    userId
                    expiresAt
                }
            }
            `,
            { userId, username, displayName, tenantId }
        );

        return result.startBiometricRegistration;
    }

    /**
     * Finish biometric registration (Step 2 of 2)
     *
     * Completes WebAuthn registration by sending the credential created by the browser.
     *
     * **Note:** tenantId already exists in backend mutation, just needs to be passed
     *
     * @param userId - User ID completing registration
     * @param tenantId - Tenant ID for backend routing
     * @param credentialJson - JSON from browser (RegisteredPublicKeyCredential)
     * @param deviceName - Optional human-readable device name
     * @returns Success status
     */
    async finishRegistration(
        userId: string,
        tenantId: string,
        credentialJson: string,
        deviceName?: string
    ): Promise<boolean> {
        const result = await this.mutation(
            `
            mutation FinishBiometricRegistration(
                $userId: String!
                $tenantId: String!
                $credentialJson: String!
                $deviceName: String
            ) {
                finishBiometricRegistration(input: {
                    userId: $userId
                    tenantId: $tenantId
                    credentialJson: $credentialJson
                    deviceName: $deviceName
                }) {
                    success
                    credentialId
                    deviceName
                    registeredAt
                }
            }
            `,
            { userId, tenantId, credentialJson, deviceName }
        );

        return result.finishBiometricRegistration.success;
    }

    /**
     * Start biometric authentication (Step 1 of 2)
     *
     * Initiates WebAuthn authentication by requesting a challenge from the backend.
     *
     * @param username - Username to authenticate
     * @param tenantId - Tenant ID for multi-tenant isolation
     * @returns Authentication challenge to pass to browser WebAuthn API
     */
    async startAuthentication(
        username: string,
        tenantId: string
    ): Promise<BiometricAuthenticationChallenge> {
        const result = await this.mutation(
            `
            mutation StartBiometricAuthentication(
                $username: String!
                $tenantId: String!
            ) {
                startBiometricAuthentication(input: {
                    username: $username
                    tenantId: $tenantId
                }) {
                    challenge
                    sessionId
                    expiresAt
                }
            }
            `,
            { username, tenantId }
        );

        return result.startBiometricAuthentication;
    }

    /**
     * Finish biometric authentication (Step 2 of 2)
     *
     * Completes WebAuthn authentication by verifying the assertion signed by the browser.
     * Returns JWT token for subsequent API requests.
     *
     * **GitHub Issue #378:** Now requires tenantId for backend routing
     *
     * @param sessionId - Session ID from startAuthentication
     * @param tenantId - Tenant ID for backend routing
     * @param assertionJson - JSON from browser (PublicKeyCredential)
     * @returns Authentication result with JWT token
     */
    async finishAuthentication(
        sessionId: string,
        tenantId: string,
        assertionJson: string
    ): Promise<BiometricAuthenticationSuccess> {
        const result = await this.mutation(
            `
            mutation FinishBiometricAuthentication(
                $sessionId: String!
                $tenantId: String!
                $assertionJson: String!
            ) {
                finishBiometricAuthentication(input: {
                    sessionId: $sessionId
                    tenantId: $tenantId
                    assertionJson: $assertionJson
                }) {
                    success
                    token
                    userId
                    tenantId
                    expiresAt
                }
            }
            `,
            { sessionId, tenantId, assertionJson }
        );

        return result.finishBiometricAuthentication;
    }

    /**
     * List user's registered biometric credentials
     *
     * @returns Array of registered devices with metadata
     */
    async listCredentials(): Promise<BiometricCredential[]> {
        const result = await this.query(
            `
            query ListBiometricCredentials {
                listBiometricCredentials {
                    id
                    deviceName
                    createdAt
                    lastUsedAt
                    counter
                }
            }
            `
        );

        return result.listBiometricCredentials || [];
    }

    /**
     * Delete registered biometric credential
     *
     * Use cases:
     * - User lost device
     * - User upgrading device
     * - Security policy requires credential rotation
     *
     * @param credentialId - Credential ID to delete
     * @returns Success status
     */
    async deleteCredential(credentialId: string): Promise<boolean> {
        const result = await this.mutation(
            `
            mutation DeleteBiometricCredential($credentialId: String!) {
                deleteBiometricCredential(credentialId: $credentialId)
            }
            `,
            { credentialId }
        );

        return result.deleteBiometricCredential;
    }

    // High-level convenience methods (combine multiple steps)

    /**
     * Complete biometric registration flow (2-step process)
     *
     * Handles both backend API interaction and browser WebAuthn API prompts.
     *
     * **GitHub Issue #378:** Now requires tenantId for backend routing
     *
     * Flow:
     * 1. Request challenge from backend (startRegistration)
     * 2. Prompt user for biometric (browser API via @simplewebauthn/browser)
     * 3. Send credential to backend (finishRegistration)
     *
     * @param userId - User ID from existing account
     * @param username - Username for display in authenticator
     * @param displayName - User-friendly display name
     * @param tenantId - Tenant ID for backend routing (DynamoDB vs PostgreSQL)
     * @param deviceName - Optional device name (e.g., "iPhone 14 Pro")
     * @returns Success status
     *
     * @throws Error if:
     * - User cancels biometric prompt
     * - Device doesn't support WebAuthn
     * - Credential already registered
     * - Network error communicating with backend
     */
    async registerBiometric(
        userId: string,
        username: string,
        displayName: string,
        tenantId: string,
        deviceName?: string
    ): Promise<boolean> {
        try {
            // Step 1: Get challenge from backend
            const { challenge } = await this.startRegistration(userId, username, displayName, tenantId);

            // Parse challenge JSON
            const challengeOptions: PublicKeyCredentialCreationOptionsJSON = JSON.parse(challenge);

            // Step 2: Prompt user for biometric (browser API)
            // This will trigger native OS prompt (Face ID, Touch ID, Windows Hello, etc.)
            const credential: RegistrationResponseJSON = await startRegistration({ optionsJSON: challengeOptions });

            // Step 3: Send credential to backend for verification
            const success = await this.finishRegistration(
                userId,
                tenantId,
                JSON.stringify(credential),
                deviceName
            );

            return success;
        } catch (error) {
            // Re-throw with more context
            if (error instanceof Error) {
                if (error.name === 'NotSupportedError') {
                    throw new Error('Biometric authentication not supported on this device');
                }
                if (error.name === 'NotAllowedError') {
                    throw new Error('Biometric registration canceled by user');
                }
                if (error.name === 'InvalidStateError') {
                    throw new Error('This device is already registered for biometric authentication');
                }
            }
            throw error;
        }
    }

    /**
     * Complete biometric authentication flow (2-step process)
     *
     * Handles both backend API interaction and browser WebAuthn API prompts.
     * Returns JWT token on successful authentication.
     *
     * Flow:
     * 1. Request challenge from backend (startAuthentication)
     * 2. Prompt user for biometric (browser API via @simplewebauthn/browser)
     * 3. Verify with backend (finishAuthentication)
     * 4. Receive JWT token for subsequent API requests
     *
     * @param username - Username to authenticate
     * @param tenantId - Tenant ID for multi-tenant isolation
     * @returns JWT token (8-hour expiry)
     *
     * @throws Error if:
     * - User cancels biometric prompt
     * - No registered credentials for user
     * - Authentication fails (invalid signature)
     * - Network error communicating with backend
     */
    async authenticateBiometric(username: string, tenantId: string): Promise<string> {
        try {
            // Step 1: Get challenge from backend
            const { challenge, sessionId } = await this.startAuthentication(username, tenantId);

            // Parse challenge JSON
            const challengeOptions: PublicKeyCredentialRequestOptionsJSON = JSON.parse(challenge);

            // Step 2: Prompt user for biometric (browser API)
            // This will trigger native OS prompt (Face ID, Touch ID, Windows Hello, etc.)
            const assertion: AuthenticationResponseJSON = await startAuthentication({ optionsJSON: challengeOptions });

            // Step 3: Verify with backend and get JWT token
            const { token } = await this.finishAuthentication(sessionId, tenantId, JSON.stringify(assertion));

            return token;
        } catch (error) {
            // Re-throw with more context
            if (error instanceof Error) {
                if (error.name === 'NotSupportedError') {
                    throw new Error('Biometric authentication not supported on this device');
                }
                if (error.name === 'NotAllowedError') {
                    throw new Error('Biometric authentication canceled by user');
                }
                if (error.name === 'InvalidStateError') {
                    throw new Error('No biometric credentials registered for this user');
                }
            }
            throw error;
        }
    }

    // ============================================================================
    // PRF Extension Methods (FERP-PII-052: User-Controlled Encryption)
    // Challenge/Response Flow (Recommended - more secure)
    // ============================================================================

    /**
     * Start PRF-enabled biometric registration (Step 1 of 2)
     *
     * FERP-PII-052: Requests registration options with PRF extension enabled.
     * The backend generates a challenge and salt, stores in Valkey with 5-minute TTL.
     *
     * @param credentialName - Human-readable name for this credential
     * @returns Full WebAuthn registration options with PRF salt
     */
    async startPrfRegistration(credentialName: string): Promise<PrfRegistrationOptions> {
        const result = await this.mutation(
            `
            mutation StartPrfRegistration($credentialName: String!) {
                startPrfRegistration(input: { credentialName: $credentialName }) {
                    sessionId
                    challenge
                    prfSalt
                    rp {
                        id
                        name
                    }
                    user {
                        id
                        name
                        displayName
                    }
                    pubKeyCredParams {
                        type
                        alg
                    }
                    timeout
                    authenticatorSelection {
                        authenticatorAttachment
                        residentKey
                        userVerification
                    }
                    attestation
                }
            }
            `,
            { credentialName }
        );

        return result.startPrfRegistration;
    }

    /**
     * Finish PRF-enabled biometric registration (Step 2 of 2)
     *
     * FERP-PII-052: Validates challenge and stores credential with PRF validation token.
     *
     * @param sessionId - Session ID from startPrfRegistration
     * @param credentialId - WebAuthn credential ID from browser
     * @param credentialName - Human-readable name for this credential
     * @param prfOutput - PRF output from client extension results (base64)
     * @param aaguid - Optional AAGUID of the authenticator
     * @param isPlatform - Whether this is a platform authenticator
     * @param userAgent - Optional user agent
     * @returns Registration result with credential info
     */
    async finishPrfRegistration(
        sessionId: string,
        credentialId: string,
        credentialName: string,
        prfOutput: string,
        aaguid?: string,
        isPlatform: boolean = false,
        userAgent?: string
    ): Promise<PrfRegistrationResult> {
        const result = await this.mutation(
            `
            mutation FinishPrfRegistration(
                $sessionId: String!
                $credentialId: String!
                $credentialName: String!
                $prfOutput: String!
                $aaguid: String
                $isPlatform: Boolean
                $userAgent: String
            ) {
                finishPrfRegistration(input: {
                    sessionId: $sessionId
                    credentialId: $credentialId
                    credentialName: $credentialName
                    prfOutput: $prfOutput
                    aaguid: $aaguid
                    isPlatform: $isPlatform
                    userAgent: $userAgent
                }) {
                    success
                    credential {
                        credentialId
                        credentialName
                        status
                        isPlatform
                        supportsPrf
                        createdAt
                    }
                    error
                }
            }
            `,
            { sessionId, credentialId, credentialName, prfOutput, aaguid, isPlatform, userAgent }
        );

        const response = result.finishPrfRegistration;
        return {
            success: response.success,
            credentialId: response.credential?.credentialId ?? '',
            deviceName: response.credential?.credentialName,
            registeredAt: response.credential?.createdAt ?? new Date().toISOString(),
            supportsPrf: response.credential?.supportsPrf ?? true,
        };
    }

    /**
     * Start PRF-enabled biometric authentication (Step 1 of 2)
     *
     * FERP-PII-052: Requests authentication options with PRF extension.
     * The backend retrieves the user's credential salt and generates a challenge.
     *
     * @returns Full WebAuthn authentication options with PRF salt
     */
    async startPrfAuthentication(): Promise<PrfAuthenticationOptions> {
        const result = await this.mutation(
            `
            mutation StartPrfAuthentication {
                startPrfAuthentication {
                    sessionId
                    challenge
                    prfSalt
                    credentialId
                    allowCredentials {
                        type
                        id
                        transports
                    }
                    timeout
                    userVerification
                }
            }
            `
        );

        return result.startPrfAuthentication;
    }

    /**
     * Finish PRF-enabled biometric authentication (Step 2 of 2)
     *
     * FERP-PII-052: Validates challenge and PRF output against stored validation token.
     *
     * @param sessionId - Session ID from startPrfAuthentication
     * @param prfOutput - PRF output from client extension results (base64)
     * @returns Verification result
     */
    async finishPrfAuthentication(
        sessionId: string,
        prfOutput: string
    ): Promise<{ isValid: boolean; credentialId: string; error?: string }> {
        const result = await this.mutation(
            `
            mutation FinishPrfAuthentication($sessionId: String!, $prfOutput: String!) {
                finishPrfAuthentication(input: {
                    sessionId: $sessionId
                    prfOutput: $prfOutput
                }) {
                    isValid
                    credentialId
                    error
                }
            }
            `,
            { sessionId, prfOutput }
        );

        return result.finishPrfAuthentication;
    }

    /**
     * Complete PRF-enabled biometric registration flow (2-step process)
     *
     * FERP-PII-052: High-level method for PRF credential enrollment.
     * Handles browser WebAuthn API with PRF extension and backend communication.
     *
     * Flow:
     * 1. Request PRF options from backend (startPrfRegistration)
     * 2. Build WebAuthn options with PRF extension
     * 3. Prompt user for biometric (browser API)
     * 4. Extract PRF output from client extension results
     * 5. Send credential + PRF output to backend (finishPrfRegistration)
     *
     * @param credentialName - Human-readable name for this credential (e.g., "iPhone 14 Pro")
     * @returns Registration result with PRF output for key derivation
     *
     * @throws Error if:
     * - User cancels biometric prompt
     * - Device doesn't support WebAuthn or PRF extension
     * - Credential already registered
     * - Network error communicating with backend
     */
    async registerPrfCredential(credentialName: string): Promise<PrfRegistrationResult> {
        try {
            // Step 1: Get PRF options from backend
            const options = await this.startPrfRegistration(credentialName);

            // Step 2: Build WebAuthn creation options
            const challengeOptions: PublicKeyCredentialCreationOptionsJSON = {
                challenge: options.challenge,
                rp: {
                    id: options.rp.id,
                    name: options.rp.name,
                },
                user: {
                    id: options.user.id,
                    name: options.user.name,
                    displayName: options.user.displayName,
                },
                pubKeyCredParams: options.pubKeyCredParams.map((p) => ({
                    type: p.type as 'public-key',
                    alg: p.alg,
                })),
                timeout: options.timeout,
                authenticatorSelection: {
                    authenticatorAttachment: options.authenticatorSelection.authenticatorAttachment as AuthenticatorAttachment | undefined,
                    residentKey: options.authenticatorSelection.residentKey as ResidentKeyRequirement,
                    userVerification: options.authenticatorSelection.userVerification as UserVerificationRequirement,
                },
                attestation: options.attestation as AttestationConveyancePreference,
            };

            // Step 3: Decode base64url salt to Uint8Array for PRF extension
            const saltBytes = this.base64urlToUint8Array(options.prfSalt);

            // Add PRF extension to the options
            const optionsWithPrf = {
                ...challengeOptions,
                extensions: {
                    prf: {
                        eval: {
                            first: saltBytes,
                        },
                    },
                },
            };

            // Step 4: Prompt user for biometric with PRF extension
            const credential = await startRegistration({
                optionsJSON: optionsWithPrf as PublicKeyCredentialCreationOptionsJSON,
            });

            // Step 5: Extract PRF output from client extension results
            const prfResults = (credential.clientExtensionResults as any)?.prf?.results;
            if (!prfResults?.first) {
                throw new Error('PRF extension not supported by this authenticator');
            }

            // Convert PRF output to base64url
            const prfOutput = this.uint8ArrayToBase64url(new Uint8Array(prfResults.first));

            // Step 6: Send credential + PRF output to backend
            const result = await this.finishPrfRegistration(
                options.sessionId,
                credential.id,
                credentialName,
                prfOutput,
                undefined, // aaguid
                true, // isPlatform (assume platform authenticator)
                navigator.userAgent
            );

            // Return result with PRF output for key derivation
            return {
                ...result,
                prfOutput,
            };
        } catch (error) {
            // Re-throw with more context
            if (error instanceof Error) {
                if (error.name === 'NotSupportedError') {
                    throw new Error('Biometric authentication not supported on this device');
                }
                if (error.name === 'NotAllowedError') {
                    throw new Error('Biometric registration canceled by user');
                }
                if (error.name === 'InvalidStateError') {
                    throw new Error('This device is already registered for biometric authentication');
                }
                if (error.message.includes('PRF')) {
                    throw new Error('Your device does not support the PRF extension required for PII encryption');
                }
            }
            throw error;
        }
    }

    /**
     * Complete PRF-enabled biometric authentication flow (2-step process)
     *
     * FERP-PII-052: High-level method for PRF authentication.
     * Returns PRF verification result for decrypting L1/L1G PII fields.
     *
     * Flow:
     * 1. Request PRF options from backend (startPrfAuthentication)
     * 2. Build WebAuthn options with PRF extension
     * 3. Prompt user for biometric (browser API)
     * 4. Extract PRF output from client extension results
     * 5. Verify with backend (finishPrfAuthentication)
     *
     * @returns Authentication result with PRF verification status and output
     *
     * @throws Error if:
     * - User cancels biometric prompt
     * - No registered credentials for user
     * - Authentication fails (invalid signature)
     * - PRF extension not supported
     * - Network error communicating with backend
     */
    async authenticateWithPrf(): Promise<{ isValid: boolean; credentialId: string; prfOutput: string; error?: string }> {
        try {
            // Step 1: Get PRF options from backend
            const options = await this.startPrfAuthentication();

            // Step 2: Build WebAuthn request options
            const challengeOptions: PublicKeyCredentialRequestOptionsJSON = {
                challenge: options.challenge,
                timeout: options.timeout,
                rpId: undefined, // Let the browser infer from origin
                allowCredentials: options.allowCredentials.map((c) => ({
                    type: c.type as 'public-key',
                    id: c.id,
                    transports: c.transports as AuthenticatorTransport[] | undefined,
                })),
                userVerification: options.userVerification as UserVerificationRequirement,
            };

            // Step 3: Decode base64url salt to Uint8Array for PRF extension
            const saltBytes = this.base64urlToUint8Array(options.prfSalt);

            // Add PRF extension to the options
            const optionsWithPrf = {
                ...challengeOptions,
                extensions: {
                    prf: {
                        eval: {
                            first: saltBytes,
                        },
                    },
                },
            };

            // Step 4: Prompt user for biometric with PRF extension
            const assertion = await startAuthentication({
                optionsJSON: optionsWithPrf as PublicKeyCredentialRequestOptionsJSON,
            });

            // Step 5: Extract PRF output from client extension results
            const prfResults = (assertion.clientExtensionResults as any)?.prf?.results;
            if (!prfResults?.first) {
                throw new Error('PRF extension not supported by this authenticator');
            }

            // Convert PRF output to base64url
            const prfOutput = this.uint8ArrayToBase64url(new Uint8Array(prfResults.first));

            // Step 6: Verify with backend
            const result = await this.finishPrfAuthentication(options.sessionId, prfOutput);

            return {
                ...result,
                prfOutput,
            };
        } catch (error) {
            // Re-throw with more context
            if (error instanceof Error) {
                if (error.name === 'NotSupportedError') {
                    throw new Error('Biometric authentication not supported on this device');
                }
                if (error.name === 'NotAllowedError') {
                    throw new Error('Biometric authentication canceled by user');
                }
                if (error.name === 'InvalidStateError') {
                    throw new Error('No biometric credentials registered for this user');
                }
                if (error.message.includes('PRF')) {
                    throw new Error('Your device does not support the PRF extension required for PII decryption');
                }
            }
            throw error;
        }
    }

    // ============================================================================
    // PRF Helper Methods
    // ============================================================================

    /**
     * Check if PRF extension is supported by the browser/authenticator
     *
     * @returns Promise<boolean> indicating PRF support
     */
    async isPrfSupported(): Promise<boolean> {
        try {
            // Check if PublicKeyCredential is available
            if (typeof PublicKeyCredential === 'undefined') {
                return false;
            }

            // Check if platform authenticator is available
            const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
            if (!available) {
                return false;
            }

            // PRF extension support can only be definitively checked during actual registration/authentication
            // For now, we assume modern platform authenticators support PRF
            // The actual check happens when we try to use it
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Convert base64url string to Uint8Array
     */
    private base64urlToUint8Array(base64url: string): Uint8Array {
        // Add padding if needed
        const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
        const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;

        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
    }

    /**
     * Convert Uint8Array to base64url string
     */
    private uint8ArrayToBase64url(bytes: Uint8Array): string {
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i] ?? 0);
        }
        const base64 = btoa(binary);
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
}

/**
 * Singleton instance for WebAuthn repository
 *
 * Use this instance throughout the application for consistent state management.
 *
 * @example
 * import { webAuthnRepository } from './repositories/WebAuthnRepository';
 *
 * // Register biometric
 * const success = await webAuthnRepository.registerBiometric(
 *     'user-123',
 *     'nurse.jane',
 *     'Nurse Jane Smith',
 *     'iPhone 14 Pro'
 * );
 *
 * // Login with biometric
 * const token = await webAuthnRepository.authenticateBiometric(
 *     'nurse.jane',
 *     'healthcare-clinic'
 * );
 *
 * // Store token for subsequent requests
 * localStorage.setItem('auth_token', token);
 */
export const webAuthnRepository = new WebAuthnRepository();

/**
 * Factory function to create a WebAuthn repository instance
 *
 * Use this when you need a fresh instance or for dependency injection.
 *
 * @returns New WebAuthnRepository instance
 */
export function createWebAuthnRepository(): WebAuthnRepository {
    return new WebAuthnRepository();
}
